import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Download } from "lucide-react";
import type {
  AdminLiabilityResponse,
  AdminReportGroupBy,
  AdminSalesReportRow,
} from "@ticketnjoy/types";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QueryError } from "@/components/query-error";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  downloadSalesReportXlsx,
  useLiabilityQuery,
  useSalesReportQuery,
  type SalesReportParams,
} from "@/features/api";
import { showApiError } from "@/lib/api-client";
import { dateTime, isoDay, istanbulToday, money } from "@/lib/format";

const MAX_RANGE_DAYS = 731;

const GROUP_BY_LABELS: Record<AdminReportGroupBy, string> = {
  Day: "Günlük",
  Week: "Haftalık",
  Month: "Aylık",
};

type Preset = "thisMonth" | "lastMonth" | "last30" | "last90" | "thisYear";

const PRESET_LABELS: Record<Preset, string> = {
  thisMonth: "Bu ay",
  lastMonth: "Geçen ay",
  last30: "Son 30 gün",
  last90: "Son 90 gün",
  thisYear: "Bu yıl",
};

function presetRange(preset: Preset): { from: string; to: string } {
  const { y, m, d } = istanbulToday();
  const today = isoDay(y, m, d);
  switch (preset) {
    case "lastMonth":
      return { from: isoDay(y, m - 1, 1), to: isoDay(y, m, 0) };
    case "last30":
      return { from: isoDay(y, m, d - 29), to: today };
    case "last90":
      return { from: isoDay(y, m, d - 89), to: today };
    case "thisYear":
      return { from: isoDay(y, 1, 1), to: today };
    default:
      return { from: isoDay(y, m, 1), to: today };
  }
}

/** "YYYY-MM-DD" yerel gün olarak; `new Date(iso)` UTC sayıp bir gün kaydırabilir. */
function parseDay(value: string): Date {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function rangeDays(from: string, to: string): number {
  return Math.round((parseDay(to).getTime() - parseDay(from).getTime()) / 86_400_000) + 1;
}

function periodLabel(period: string, groupBy: AdminReportGroupBy): string {
  const start = parseDay(period);
  if (groupBy === "Month") {
    return start.toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
  }
  if (groupBy === "Week") {
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
    const fmt = (d: Date) => d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short" });
    return `${fmt(start)} – ${fmt(end)} ${end.getFullYear()}`;
  }
  return start.toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric", weekday: "short" });
}

const GROUP_BY_VALUES: AdminReportGroupBy[] = ["Day", "Week", "Month"];

/** Aralık URL'de (from/to/groupBy); yoksa bu ay, günlük. */
export function ReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaults = presetRange("thisMonth");
  const rawGroupBy = searchParams.get("groupBy") as AdminReportGroupBy | null;
  const params: SalesReportParams = {
    from: searchParams.get("from") ?? defaults.from,
    to: searchParams.get("to") ?? defaults.to,
    groupBy: rawGroupBy && GROUP_BY_VALUES.includes(rawGroupBy) ? rawGroupBy : "Day",
  };

  const validDates = /^\d{4}-\d{2}-\d{2}$/.test(params.from) && /^\d{4}-\d{2}-\d{2}$/.test(params.to);
  const days = validDates ? rangeDays(params.from, params.to) : 0;
  const rangeError = !validDates
    ? "Geçerli bir tarih aralığı seç."
    : days < 1
      ? "Bitiş tarihi başlangıçtan önce olamaz."
      : days > MAX_RANGE_DAYS
        ? "En fazla 2 yıllık aralık seçilebilir."
        : null;

  const sales = useSalesReportQuery(params, rangeError === null);
  const liability = useLiabilityQuery();
  const [downloading, setDownloading] = useState(false);

  const activePreset = (Object.keys(PRESET_LABELS) as Preset[]).find((p) => {
    const r = presetRange(p);
    return r.from === params.from && r.to === params.to;
  });

  function update(next: Partial<SalesReportParams>) {
    const merged = { ...params, ...next };
    const search = new URLSearchParams();
    search.set("from", merged.from);
    search.set("to", merged.to);
    if (merged.groupBy !== "Day") search.set("groupBy", merged.groupBy);
    setSearchParams(search, { replace: true });
  }

  async function onDownload() {
    setDownloading(true);
    try {
      await downloadSalesReportXlsx(params);
    } catch (err) {
      showApiError(err, "Excel indirilemedi.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Raporlar</h1>
          <p className="text-sm text-muted-foreground">
            Tüm organizatörlerin satışları ve TicketNjoy'un organizatörlere borcu.
          </p>
        </div>
        <Button onClick={onDownload} disabled={downloading || rangeError !== null}>
          <Download className="size-4" />
          {downloading ? "Hazırlanıyor..." : "Excel indir"}
        </Button>
      </div>

      <Card>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {(Object.keys(PRESET_LABELS) as Preset[]).map((p) => (
              <Button
                key={p}
                size="sm"
                variant={activePreset === p ? "default" : "outline"}
                onClick={() => update(presetRange(p))}
              >
                {PRESET_LABELS[p]}
              </Button>
            ))}
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="report-from">Başlangıç</Label>
              <Input
                id="report-from"
                type="date"
                value={params.from}
                onChange={(e) => e.target.value && update({ from: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="report-to">Bitiş</Label>
              <Input
                id="report-to"
                type="date"
                value={params.to}
                onChange={(e) => e.target.value && update({ to: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Kırılım</Label>
              <Select
                value={params.groupBy}
                onValueChange={(v) => update({ groupBy: v as AdminReportGroupBy })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GROUP_BY_VALUES.map((g) => (
                    <SelectItem key={g} value={g}>
                      {GROUP_BY_LABELS[g]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {rangeError ? (
            <p className="text-xs text-destructive">{rangeError}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Siparişler ödeme tarihine göre (Türkiye saati) döneme yazılır; sonradan yapılan
              iadeler satışın dönemine düşülür.
            </p>
          )}
        </CardContent>
      </Card>

      {rangeError === null && (
        <>
          {sales.data && sales.data.totals.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {sales.data.totals.flatMap((t) => [
                <SummaryTile key={`${t.currency}-net`} label={`Net satış · ${t.currency}`} value={money(t.netSales, t.currency)} />,
                <SummaryTile
                  key={`${t.currency}-rev`}
                  label={`Platform geliri · ${t.currency}`}
                  value={money(t.platformRevenue, t.currency)}
                  hint={`Komisyon ${money(t.commission, t.currency)} + servis ${money(t.serviceFee, t.currency)}`}
                />,
                <SummaryTile key={`${t.currency}-ord`} label={`Sipariş · ${t.currency}`} value={t.orderCount.toLocaleString("tr-TR")} />,
                <SummaryTile key={`${t.currency}-tkt`} label={`Bilet · ${t.currency}`} value={t.ticketCount.toLocaleString("tr-TR")} />,
              ])}
            </div>
          )}

          <Card className="py-0">
            <CardContent className="px-0">
              {sales.error ? (
                <QueryError
                  className="m-6"
                  message="Rapor yüklenemedi."
                  onRetry={() => sales.refetch()}
                  retrying={sales.isFetching}
                />
              ) : sales.isLoading ? (
                <p className="p-6 text-sm text-muted-foreground">Yükleniyor...</p>
              ) : sales.data && sales.data.rows.length === 0 ? (
                <p className="p-6 text-sm text-muted-foreground">Bu aralıkta satış yok.</p>
              ) : (
                sales.data && (
                  <div className={sales.isFetching ? "overflow-x-auto opacity-60" : "overflow-x-auto"}>
                    <SalesTable rows={sales.data.rows} totals={sales.data.totals} groupBy={sales.data.groupBy} />
                  </div>
                )
              )}
            </CardContent>
          </Card>
        </>
      )}

      <LiabilityCard
        data={liability.data}
        isLoading={liability.isLoading}
        error={!!liability.error}
        onRetry={() => liability.refetch()}
        retrying={liability.isFetching}
      />
    </div>
  );
}

function SummaryTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card className="gap-1 py-4">
      <CardContent className="space-y-1 px-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-xl font-semibold tabular-nums">{value}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function SalesTable({
  rows,
  totals,
  groupBy,
}: {
  rows: AdminSalesReportRow[];
  totals: AdminSalesReportRow[];
  groupBy: AdminReportGroupBy;
}) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b text-left text-xs text-muted-foreground">
          <th className="px-4 py-3 font-medium">Dönem</th>
          <th className="px-4 py-3 text-right font-medium">Sipariş</th>
          <th className="px-4 py-3 text-right font-medium">Bilet</th>
          <th className="px-4 py-3 text-right font-medium">Brüt</th>
          <th className="px-4 py-3 text-right font-medium">İade</th>
          <th className="px-4 py-3 text-right font-medium">Net satış</th>
          <th className="px-4 py-3 text-right font-medium">Komisyon</th>
          <th className="px-4 py-3 text-right font-medium">Servis</th>
          <th className="px-4 py-3 text-right font-medium">Platform geliri</th>
          <th className="px-4 py-3 text-right font-medium">Organizatör payı</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <SalesRow
            key={`${r.period}-${r.currency}`}
            label={periodLabel(r.period, groupBy)}
            row={r}
            showCurrency={totals.length > 1}
          />
        ))}
        {totals.map((t) => (
          <SalesRow
            key={`total-${t.currency}`}
            label={totals.length > 1 ? `Toplam · ${t.currency}` : "Toplam"}
            row={t}
            showCurrency={false}
            isTotal
          />
        ))}
      </tbody>
    </table>
  );
}

function SalesRow({
  label,
  row,
  showCurrency,
  isTotal = false,
}: {
  label: string;
  row: AdminSalesReportRow;
  showCurrency: boolean;
  isTotal?: boolean;
}) {
  const c = row.currency;
  return (
    <tr className={isTotal ? "border-t-2 bg-muted/40 font-semibold" : "border-b last:border-0 hover:bg-muted/40"}>
      <td className="px-4 py-2.5 whitespace-nowrap">
        {label}
        {showCurrency && <span className="ml-2 text-xs text-muted-foreground">{c}</span>}
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums">{row.orderCount.toLocaleString("tr-TR")}</td>
      <td className="px-4 py-2.5 text-right tabular-nums">{row.ticketCount.toLocaleString("tr-TR")}</td>
      <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">{money(row.grossSales, c)}</td>
      <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap text-muted-foreground">
        {money(row.refunded, c)}
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">{money(row.netSales, c)}</td>
      <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">{money(row.commission, c)}</td>
      <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">{money(row.serviceFee, c)}</td>
      <td className="px-4 py-2.5 text-right font-semibold tabular-nums whitespace-nowrap">
        {money(row.platformRevenue, c)}
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">{money(row.organizerNet, c)}</td>
    </tr>
  );
}

function LiabilityCard({
  data,
  isLoading,
  error,
  onRetry,
  retrying,
}: {
  data: AdminLiabilityResponse | undefined;
  isLoading: boolean;
  error: boolean;
  onRetry: () => void;
  retrying: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Organizatörlere borç</CardTitle>
        <CardDescription>
          Şu an itibarıyla bekleyen (etkinliği bitmemiş) + çekilebilir + ödeme sürecindeki
          tutarlar. Eksi tutar avanstır, organizatör borçlu demektir.
          {data && ` Hesaplama: ${dateTime(data.generatedAt)}.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {error ? (
          <QueryError message="Bakiyeler yüklenemedi." onRetry={onRetry} retrying={retrying} />
        ) : isLoading || !data ? (
          <p className="text-sm text-muted-foreground">Yükleniyor...</p>
        ) : data.organizers.length === 0 ? (
          <p className="text-sm text-muted-foreground">Açık bakiye yok.</p>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {data.totals.flatMap((t) => [
                <SummaryTile
                  key={`${t.currency}-total`}
                  label={`Toplam borç · ${t.currency}`}
                  value={money(t.total, t.currency)}
                  hint={`${t.organizerCount} organizatör`}
                />,
                <SummaryTile key={`${t.currency}-pending`} label="Bekleyen" value={money(t.pending, t.currency)} />,
                <SummaryTile key={`${t.currency}-available`} label="Çekilebilir" value={money(t.available, t.currency)} />,
                <SummaryTile key={`${t.currency}-progress`} label="Ödeme sürecinde" value={money(t.inProgress, t.currency)} />,
              ])}
            </div>
            <div className="max-h-[480px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card">
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Organizatör</th>
                    <th className="py-2 pr-4 text-right font-medium">Bekleyen</th>
                    <th className="py-2 pr-4 text-right font-medium">Çekilebilir</th>
                    <th className="py-2 pr-4 text-right font-medium">Süreçte</th>
                    <th className="py-2 text-right font-medium">Toplam</th>
                  </tr>
                </thead>
                <tbody>
                  {data.organizers.map((o) => (
                    <tr key={`${o.organizerId}-${o.currency}`} className="border-b last:border-0">
                      <td className="py-2 pr-4">
                        <Link to={`/organizers/${o.organizerId}`} className="font-medium hover:underline">
                          {o.organizerName}
                        </Link>
                        {data.totals.length > 1 && (
                          <span className="ml-2 text-xs text-muted-foreground">{o.currency}</span>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">{money(o.pending, o.currency)}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{money(o.available, o.currency)}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{money(o.inProgress, o.currency)}</td>
                      <td className="py-2 text-right font-semibold tabular-nums">{money(o.total, o.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
